import React, { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import intl from "react-intl-universal";

import commonUtils from "../../../../script/utils/commonUtils";

import worstcaseRest from "../../../../rest/worstcaseRest";
import wattwaechterRest from "../../../../rest/wattwaechterRest";
import calcAreaUtils from "../../../../script/utils/calcAreaUtils";
import plotUtils from "../../../../script/utils/plotUtils";
import { Vector3D } from "../../../../script/lod2";

const LegendStyle = styled.div`
    z-index: 999;
    display: flex;
    width: stretch;
    font-size: 14px;
    flex-wrap: wrap;
    position: absolute;
    background-color: white;
    margin: -15px 15px 1px 15px;
    justify-content: space-between;
`;

export const CalculationType = Object.freeze({
    WATTWAECHTER: "WATTWAECHTER",
    WORSTCASE: "WORSTCASE",
});

export const Legend = props => {
    const { portal, legendData, viewType, calcSetting, plot } = props;

    const [data, setData] = useState();

    const rotatePoint = (x, y, rotationDeg) => {
        if (rotationDeg === 0) {
            return [x, y];
        }
        const theta = rotationDeg * (Math.PI / 180); // Convert to radians
        const rotatedX = Math.cos(theta) * x + Math.sin(theta) * y;
        const rotatedY = - Math.sin(theta) * x + Math.cos(theta) * y;
        return [rotatedX, rotatedY];
    };

    const onMouseMove = useCallback(
        commonUtils.debounce(150, async (opt, image, calculation, slice = 0, fieldMax = false) => {
            // If image is clipped and the mouse pointer is outside of the clipped area, do nothing
            if (!plotUtils.isPointerWithiImageClipPath(opt, plot)) {
                setData();
                return;
            }

            const { calcArea, calculationType, calculationSetting } = calculation;
            const preferFarField = calculationSetting?.preferFarField;
            const { originX, originY, originZ } = calcArea;
            const { lengthU, lengthV, angle } = calcAreaUtils.getCalcAreaBaseProperties2D(calcArea);
            const lengthV3D = Math.sqrt(calcArea.vaxisX * calcArea.vaxisX + calcArea.vaxisY * calcArea.vaxisY + calcArea.vaxisZ * calcArea.vaxisZ);

            const { environmentFactor, sliceDist } = calcSetting;
            let { fieldType } = calcSetting;
            const isWorstcaseLikeCalculation = [
                CalculationType.WORSTCASE,
            ].includes(calculationType);

            if (isWorstcaseLikeCalculation) {
                fieldType = "EXHAUSTION_FACTOR_BLMSCHV";
            }

            let result = {};
            let getFieldInterpolated;
            if (isWorstcaseLikeCalculation) {
                result = calculation.worstcaseResult;
                 
                getFieldInterpolated = worstcaseRest.getFieldInterpolatedInSlice;
            } else if (calculationType === "WATTWAECHTER") {
                result = calculation.wattwaechterResult;
                getFieldInterpolated = wattwaechterRest.getFieldInterpolatedInSlice;
            } else {
                console.error("Unsupported calculation type:", calculationType);
                return;
            }

            const { numberU, numberV } = result;

            const {ratioU, ratioV} = plotUtils.calculteAreaSidesClickRatio(opt);

            const u = Math.min(Math.max(Math.round(ratioU * (numberU - 1)), 0), numberU - 1);
            const v = Math.min(Math.max(Math.round(ratioV * (numberV - 1)), 0), numberV - 1);

            const fieldInterpolated = await getFieldInterpolated(calculation.id, u, v, slice, fieldMax);

            const [rotatedX, rotatedY] = rotatePoint((u / numberU) * lengthU, (v / numberV) * lengthV, angle);
            const sliceOffset = fieldMax
                ? new Vector3D(0,0,0)
                : calcAreaUtils.calculateSliceOffsetVector(calcArea, slice, sliceDist);
            const calcX = rotatedX + originX + sliceOffset.x;
            const calcY = rotatedY + originY + sliceOffset.y;
            let calcV = "--";
            if (fieldInterpolated === "NaN") {
                calcV = "--";
            } else {
                calcV = fieldInterpolated.toFixed(2);
                if (["PROTECTION_AREA_BLMSCHV", "PROTECTION_AREA_EXP_1"].includes(fieldType)) {
                    calcV = fieldInterpolated > 0.5 ? "in" : "out";
                } else if (fieldType === "E_FIELD") {
                    calcV += " V/m";
                } else if (fieldType === "H_FIELD") {
                    calcV += " A/m";
                }
            }


            const height = originZ + (v + 0.5) / numberV * calcArea.vaxisZ + sliceOffset.z;
            const resolution = Math.max(lengthU / (numberU - 1), lengthV3D / (numberV - 1));

            setData({
                lengthU, lengthV3D, originX, originY, height, angle,
                environmentFactor, preferFarField,
                calcX, calcY, calcV, resolution,
            });
        }),
        [JSON.stringify(calcSetting), viewType, plot]
    );

    useEffect(() => {
        if (legendData) {
            const { opt, image, calculation, slice, fieldMax } = legendData;
            onMouseMove(opt, image, calculation, slice, fieldMax);
        }
    }, [legendData, onMouseMove]);

    const buildLegend = () => {
        let div1 = "";
        let div2 = "";
        let div3 = "";
        let div4 = "";
        let div5 = "";
        let div6 = "";
        let div7 = "";

        if (data) {
            const {
                lengthU, lengthV3D, originX, originY, height,
                environmentFactor, preferFarField,
                calcX, calcY, calcV, resolution,
            } = data;

            div1 = `(${originX.toFixed(2)}m, ${originY.toFixed(2)}m) h=${(height).toFixed(2)}m`;
            div2 = `dim=(${lengthU.toFixed(2)}X${lengthV3D.toFixed(2)})m`;
            if (environmentFactor !== undefined && environmentFactor !== null) {
                div3 = `ef=${environmentFactor.toFixed(4)}`;
            }
            if (preferFarField !== undefined && preferFarField !== null) {
                div4 = intl.get(`entry.${preferFarField ? "farField" : "nearField"}`);
            }
            div5 = `(${calcX.toFixed(2)}m, ${calcY.toFixed(2)}m) h=${(height).toFixed(2)}m`;
            div6 = calcV;
            div7 = `res=${resolution.toFixed(2)}m`;
        }
        if (legendData) {
            return (
                <LegendStyle>
                    <div>{div1}</div>
                    <div>{div2}</div>
                    <div>{div3}</div>
                    <div>{div4}</div>
                    <div>{div5}</div>
                    <div>{div6}</div>
                    <div>{div7}</div>
                </LegendStyle>
            );
        }
        return <></>;
    };

    return portal
        ? createPortal(buildLegend(), document.getElementById("plan-legend"))
        : buildLegend();
};


